import type { DrawerDialogPayload } from '../hooks/useDrawerDialog'

/**
 * Format a timeline activity item into a title + body suitable for the
 * DrawerDialog.  Returns null for item types that have no meaningful
 * detail view (the classic UI's detail modals cover those).
 */
export function formatTimelineItemDetail(item: any): DrawerDialogPayload | null {
  if (!item) return null
  const type = String(item.type || '').trim()
  const data = item.data && typeof item.data === 'object' ? item.data : {}
  const at = formatTimestamp(item.created_at)

  if (type === 'story') {
    return {
      title: String(data.agent_name || 'Agent Story'),
      body: lines([data.sentence, at]),
    }
  }

  if (type === 'task') {
    return {
      title: String(data.summary || data.task_type || 'Task'),
      body: lines([
        field('Status', data.status),
        field('Body', data.body),
        at,
      ]),
    }
  }

  if (type === 'reachout_email') {
    return {
      title: String(data.subject || 'Email'),
      body: lines([
        field('To', data.to),
        field('Status', data.status),
        field('Body', data.body || data.plain_body),
        at,
      ]),
      maxWidth: 720,
    }
  }

  if (type === 'reachout_call') {
    return {
      title: 'Call',
      body: lines([
        field('To', data.to),
        field('Status', data.status),
        field('Script', data.call_script),
        at,
      ]),
    }
  }

  if (type === 'reachout_voicemail') {
    return {
      title: 'Voicemail',
      body: lines([
        field('To', data.to),
        field('Status', data.status),
        field('Script', data.voicemail_script),
        at,
      ]),
    }
  }

  if (type === 'reachout_linkedin_connect') {
    return {
      title: 'LinkedIn Connect',
      body: lines([
        field('To', data.to),
        field('Status', data.status),
        field('Note', data.connection_note),
        at,
      ]),
    }
  }

  if (type === 'followup') {
    return {
      title: String(data.agent_name || 'Followup'),
      body: lines([
        field('Reason', data.reason),
        field('Status', data.status),
        at,
      ]),
    }
  }

  if (type === 'crm_update') {
    return {
      title: String(data.title || 'CRM Update'),
      body: lines([
        field('Status', data.status),
        data.subtitle,
        at,
      ]),
    }
  }

  if (type === 'signal') {
    const desc = data.description && typeof data.description === 'object' ? data.description : {}
    return {
      title: String(data.display_name || data.name || 'Signal'),
      body: lines([
        desc.subject,
        desc.body || desc.summary || desc.text,
        field('From', desc.from_email || desc.from_header),
        field('To', desc.to),
        at,
      ]),
    }
  }

  if (type.startsWith('salesforce_email')) {
    return {
      title: String(data.clean_subject || data.subject || 'Email'),
      body: lines([
        field('From', data.from || data.who_name || data.who_email),
        field('To', data.to),
        data.body || data.text_body,
        at,
      ]),
      maxWidth: 720,
    }
  }

  if (type === 'salesforce_event') {
    return {
      title: String(data.subject || 'Event'),
      body: lines([
        field('With', data.who_name || data.who_email),
        field('Start', data.start_date_time || data.activity_date),
        field('Location', data.location),
        data.description,
        at,
      ]),
    }
  }

  if (type === 'salesforce_campaign_member') {
    return {
      title: String(data.campaign_name || 'Campaign'),
      body: lines([
        field('Contact', data.contact_name || data.contact_email),
        field('Status', data.status),
        at,
      ]),
    }
  }

  if (type === 'hubspot_email_in' || type === 'hubspot_email_out') {
    const contactName = data.contact_info?.name
    // from_name/to_name come from reclassified task emails (parsed header);
    // from_email/to_email come from native HubSpot email objects.
    const fromLabel = data.from_name
      ? `${data.from_name}${data.from_email ? ` <${data.from_email}>` : ''}`
      : data.from_email
    const toLabel = data.to_name
      ? `${data.to_name}${data.to_email ? ` <${data.to_email}>` : ''}`
      : data.to_email
    // body comes from parsed task body; body_preview from native emails.
    const bodyContent = data.body || data.body_preview
    return {
      title: String(data.clean_subject || data.subject || 'Email'),
      body: lines([
        field('Contact', contactName),
        field('From', fromLabel),
        field('To', toLabel),
        field('Status', data.status || data.outcome),
        bodyContent,
        at,
      ]),
      maxWidth: 720,
    }
  }

  if (type === 'hubspot_call') {
    const contactName = data.contact_info?.name
    return {
      title: String(data.clean_subject || data.subject || 'Call'),
      body: lines([
        field('Contact', contactName),
        field('Outcome', data.outcome),
        data.body,
        at,
      ]),
    }
  }

  if (type === 'hubspot_meeting') {
    const contactName = data.contact_info?.name
    return {
      title: String(data.clean_subject || data.subject || 'Meeting'),
      body: lines([
        field('Contact', contactName),
        field('Outcome', data.outcome),
        data.body,
        at,
      ]),
    }
  }

  if (type === 'hubspot_task') {
    return {
      title: String(data.clean_subject || data.subject || 'Task'),
      body: lines([
        field('Status', data.outcome),
        data.body,
        at,
      ]),
    }
  }

  if (type === 'hubspot_note' || type === 'hubspot_engagement') {
    return {
      title: humanize(data.engagement_type || type),
      body: lines([
        data.clean_subject || data.subject,
        data.body,
        at,
      ]),
    }
  }

  if (type === 'pipedrive_email_in' || type === 'pipedrive_email_out') {
    const contactName = data.contact_info?.name
    const from = ((data.from_ || [])[0]) || {}
    const to = ((data.to || [])[0]) || {}
    return {
      title: String(data.clean_subject || data.subject || 'Email'),
      body: lines([
        field('Contact', contactName),
        field('From', from.email_address || from.name),
        field('To', to.email_address || to.name),
        data.snippet || data.body,
        at,
      ]),
      maxWidth: 720,
    }
  }

  if (type === 'pipedrive_call' || type === 'pipedrive_meeting' || type === 'pipedrive_task' || type === 'pipedrive_activity') {
    const contactName = data.contact_info?.name
    const note = String(data.note || data.public_description || '').replace(/<[^>]*>/g, '').trim()
    return {
      title: String(data.clean_subject || data.subject || humanize(type)),
      body: lines([
        field('Contact', contactName),
        field('Type', data.type),
        field('Done', data.done ? 'Yes' : 'No'),
        field('Due', data.due_date),
        field('Duration', data.duration),
        field('Location', data.location),
        note,
        at,
      ]),
    }
  }

  if (type === 'gong_call') {
    const counts = data.participant_summary?.counts || {}
    return {
      title: String(data.title || 'Gong Call'),
      body: lines([
        field('Direction', data.direction),
        field('Participants', `${counts.internal || 0} internal, ${counts.external || 0} external`),
        field('Duration', data.duration),
        at,
      ]),
    }
  }

  if (type === 'prospect_lifecycle_transition') {
    const triggeredBy = data.actor_user_name || _agentTriggeredBy(data)
    return {
      title: 'Lifecycle Transition',
      body: lines([
        field('From', data.from_state),
        field('To', data.to_state),
        field('Reason', data.reason),
        field('Triggered by', triggeredBy),
        at,
      ]),
    }
  }

  if (type === 'sales_play_status_change') {
    const triggeredBy = data.actor_user_name || _agentTriggeredBy(data)
    return {
      title: `Sales Play: ${data.sales_play_name || 'Status Change'}`,
      body: lines([
        field('Action', data.kind),
        field('Reason', data.reason),
        field('Detail', data.detail),
        field('Triggered by', triggeredBy),
        at,
      ]),
    }
  }

  // Generic fallback — dump all data fields
  const fallbackLines = Object.entries(data)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => field(humanize(k), typeof v === 'object' ? JSON.stringify(v) : String(v)))
  fallbackLines.push(at)
  return {
    title: humanize(type) || 'Activity',
    body: lines(fallbackLines),
  }
}

function field(label: string, value: any): string {
  const v = String(value || '').trim()
  if (!v) return ''
  return `${label}: ${v}`
}

function lines(parts: (string | undefined | null)[]): string {
  return parts.filter((p) => typeof p === 'string' && p.trim()).join('\n')
}

function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleString([], {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return ''
  }
}

function humanize(s: string): string {
  return s.replace(/[_-]+/g, ' ').replace(/\b(\w)/g, (m) => m.toUpperCase())
}

const _RUN_TYPE_LABELS: Record<string, string> = {
  source: 'Find', filter: 'Qualify', agent: 'Agent', chat: 'Chat', revision: 'Revision',
}

function _agentTriggeredBy(data: any): string {
  const parts = [data.agent_name, _RUN_TYPE_LABELS[data.agent_run_type] || data.agent_run_type].filter(Boolean)
  return parts.join(' · ')
}
